import { usePropertiesStore } from "../stores/propertiesStore";
import { elementWorldBox } from "../viewer/geometry/rayCast";
import { is2dViewer } from "../viewer/geometry/viewMode";

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

function nameMatchScore(nodeName: string, targetName: string): number {
  const node = normalizeName(nodeName);
  const target = normalizeName(targetName);
  if (!node || !target) return 0;
  if (node === target) return 100;
  if (node.includes(target) || target.includes(node)) return 75;
  const tokens = target.split(/\s+/).filter(t => t.length > 1);
  if (tokens.length > 0 && tokens.every(t => node.includes(t))) return 55;
  // Room tags on F2D often show only the number ("101") while 3D uses "Bedroom 101".
  // Require 3+ digits so "2" does not match sheet title "A2.1" graphics.
  if (/^\d{3,4}$/.test(target) && node.includes(target)) return 60;
  return 0;
}

/** Single-digit / two-digit keys match sheet numbers, grid bubbles, and dimensions. */
function isRiskySearchKey(key: string): boolean {
  const k = key.trim();
  if (/^\d{1,2}$/.test(k)) return true;
  if (/^a\d/i.test(k)) return true;
  return false;
}

/** Reject annotation/text crumbs on F2D — keep room-sized footprints. */
function footprintUsable(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number
): boolean {
  const box = elementWorldBox(viewer, dbId);
  const modelBox = viewer.model?.getBoundingBox?.();
  if (!box || !modelBox) return !!box;

  const elem = box.getSize(new THREE.Vector3());
  const model = modelBox.getSize(new THREE.Vector3());
  const elemArea = Math.max(elem.x, 0) * Math.max(elem.y, 0);
  const modelArea = Math.max(model.x, 0) * Math.max(model.y, 0);
  if (modelArea <= 0) return true;

  const ratio = elemArea / modelArea;
  if (is2dViewer(viewer)) {
    return ratio >= 0.002;
  }
  return ratio >= 0.00005;
}

function searchKeysForElement(
  extractionDbId: number,
  displayName?: string
): string[] {
  const keys = new Set<string>();
  if (displayName?.trim()) keys.add(displayName.trim());
  const props = usePropertiesStore.getState().get(extractionDbId);
  if (props?.name) keys.add(props.name);
  for (const p of props?.properties ?? []) {
    if (
      p.displayName === "Number" ||
      p.displayName === "Room Number" ||
      p.displayName === "Name" ||
      p.displayName === "Room Name"
    ) {
      const v = String(p.displayValue ?? "").trim();
      if (v) keys.add(v);
    }
  }
  const num = displayName?.match(/\b(\d{3,4})\b/)?.[1];
  if (num) keys.add(num);
  return [...keys].filter(k => !isRiskySearchKey(k));
}

/**
 * Map an extraction-time dbId (usually from the 3D viewable) to a dbId that
 * exists on the currently loaded sheet. Forge assigns different dbIds per
 * viewable; name-based lookup bridges 3D extraction ↔ 2D sheet highlighting.
 */
export function resolveViewerDbId(
  viewer: Autodesk.Viewing.GuiViewer3D,
  extractionDbId: number,
  displayName?: string
): number | null {
  if (elementWorldBox(viewer, extractionDbId)) {
    return extractionDbId;
  }

  const searchKeys = searchKeysForElement(extractionDbId, displayName);
  if (searchKeys.length === 0) return null;

  const tree = viewer.model?.getInstanceTree();
  if (!tree) return null;

  let bestDbId: number | null = null;
  let bestScore = 0;
  const consider = (dbId: number) => {
    const nodeName = tree.getNodeName(dbId) ?? "";
    let score = 0;
    for (const key of searchKeys) {
      score = Math.max(score, nameMatchScore(nodeName, key));
    }
    if (score <= 0) return;
    if (!footprintUsable(viewer, dbId)) return;
    if (score > bestScore) {
      bestScore = score;
      bestDbId = dbId;
    }
  };

  tree.enumNodeChildren(tree.getRootId(), consider, true);
  if (bestDbId != null) {
    console.debug(
      "[Vera] Resolved dbId",
      extractionDbId,
      "→",
      bestDbId,
      "on current sheet via name",
      displayName
    );
    return bestDbId;
  }
  return null;
}
